import { useMutation, useQuery } from '@tanstack/react-query';

import cycleEvaluationService from './cycleEvaluationService';

import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';

import { queryClient } from '@/AppWithProviders';
import { CycleEvaluationGroupQueryKey, CycleEvaluationQueryKey } from '@/constants/QueryKeys';

const useGetCycleEvaluation = (id: string) => {
    return useQuery<CycleEvaluation>({
        queryKey: [CycleEvaluationQueryKey.CYCLE_EVALUATION, id],
        queryFn: () => cycleEvaluationService.getCycleEvaluation(id),
        enabled: !!id,
    });
};

const useGetCycleEvaluationList = (options?: { enabled?: boolean }) => {
    return useQuery<CycleEvaluation[]>({
        queryKey: [CycleEvaluationQueryKey.CYCLE_EVALUATION_LIST],
        queryFn: () => cycleEvaluationService.getCycleEvaluationList(),
        enabled: options?.enabled ?? true,
    });
};

const useCreateCycleEvaluation = () => {
    return useMutation<CycleEvaluation, Error, EvaluationTypeValue>({
        mutationFn: (evaluationType: EvaluationTypeValue) =>
            cycleEvaluationService.createCycleEvaluation(evaluationType),
    });
};

const useUpdateCycleEvaluation = () => {
    return useMutation<CycleEvaluation, Error, CycleEvaluation>({
        mutationFn: (cycleEvaluation: CycleEvaluation) =>
            cycleEvaluationService.updateCycleEvaluation(cycleEvaluation),
        onSuccess: () =>
            Promise.all([
                queryClient.invalidateQueries({
                    queryKey: [CycleEvaluationQueryKey.CYCLE_EVALUATION],
                }),
                queryClient.invalidateQueries({
                    queryKey: [CycleEvaluationQueryKey.CYCLE_EVALUATION_LIST],
                }),
                queryClient.invalidateQueries({
                    queryKey: [CycleEvaluationGroupQueryKey.CYCLE_EVALUATION_GROUP],
                }),
            ]),
    });
};

export interface CreateCycleEvaluationForGroupVariables {
    reviewType: EvaluationTypeValue;
    cycleEvaluationGroupId: string;
}

const useCreateCycleEvaluationForGroup = () => {
    return useMutation<CycleEvaluation, Error, CreateCycleEvaluationForGroupVariables>({
        mutationFn: ({ reviewType, cycleEvaluationGroupId }) =>
            cycleEvaluationService.createCycleEvaluation(reviewType, cycleEvaluationGroupId),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CycleEvaluationGroupQueryKey.CYCLE_EVALUATION_GROUP],
            }),
    });
};

const useDeleteCycleEvaluation = () => {
    return useMutation<void, Error, CycleEvaluation>({
        mutationFn: (cycleEvaluation: CycleEvaluation) =>
            cycleEvaluationService.deleteCycleEvaluation(cycleEvaluation.id),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CycleEvaluationQueryKey.CYCLE_EVALUATION_LIST],
            }),
    });
};

const useLaunchCycleEvaluation = () => {
    return useMutation<void, Error, CycleEvaluation>({
        mutationFn: (cycleEvaluation: CycleEvaluation) =>
            cycleEvaluationService.launchCycleEvaluation(cycleEvaluation.id),
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: [CycleEvaluationQueryKey.CYCLE_EVALUATION_LIST],
            }),
    });
};

export {
    useGetCycleEvaluation,
    useGetCycleEvaluationList,
    useCreateCycleEvaluation,
    useCreateCycleEvaluationForGroup,
    useUpdateCycleEvaluation,
    useDeleteCycleEvaluation,
    useLaunchCycleEvaluation,
};
